import { ReactElement } from 'react'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { ClientAgentReference } from '@/agent/references/store/schemas'
import CitationSourceTooltipContent from './CitationSourceTooltipContent'

/** PRD §5.1: the hover tooltip appears after ~1s. */
const kEnterDelayMs = 1000

type CitationSourceTooltipProps = {
  reference: ClientAgentReference
  /** Footnote label ([1], [1.2]); omitted when wrapping a legal citation link. */
  citationLabel?: string
  caseStatus: string | null | undefined
  isOpening: boolean
  error: string | null
  /** Fired when the tooltip opens; the hook uses it for citator impression tracking. */
  onOpen: () => void
  onViewReference: () => void
  onViewCitationAnalysis: () => void
  /** The citation trigger element (legal citation link or numbered badge). */
  children: ReactElement
}

/**
 * Hover tooltip for a chat citation (~1s delay): source title, metadata, and the
 * citator case-status treatment. Deliberately a tooltip, not a popover — nothing
 * toggles on click; clicking the citation opens the source directly (PR #96 behavior).
 *
 * Touch devices never see it (disableTouchListener): with no hover, a tap must mean
 * "open the source", so a click-toggled tooltip would fight the click-through.
 */
export default function CitationSourceTooltip(props: CitationSourceTooltipProps) {
  const { reference, citationLabel, caseStatus, isOpening, error, onOpen, onViewReference, onViewCitationAnalysis, children } = props

  return (
    <CustomizableTooltip
      enterDelay={kEnterDelayMs}
      // Without this, MUI drops the delay to 0 for a while after any tooltip opens —
      // sweeping the cursor across citation-dense prose would pop tooltips instantly.
      enterNextDelay={kEnterDelayMs}
      disableTouchListener
      onOpen={onOpen}
      // Lift MUI's default 300px bubble cap; the content div carries the same
      // responsive width caps the retired citation popover used.
      classes={{ tooltip: '!max-w-none' }}
      title={
        <CitationSourceTooltipContent
          reference={reference}
          citationLabel={citationLabel}
          caseStatus={caseStatus}
          isOpening={isOpening}
          error={error}
          onViewReference={onViewReference}
          onViewCitationAnalysis={onViewCitationAnalysis}
        />
      }
    >
      {children}
    </CustomizableTooltip>
  )
}
